import { app, platform, session, systemPreferences } from "@todesktop/client-core";

/**
 * Screen-share plumbing for the toDesktop (Electron) build.
 *
 * Electron has no built-in screen picker: `getDisplayMedia` only resolves once a
 * display-media request handler has been registered that names the source to
 * capture. The browser build never touches any of this — the native picker
 * handles it — so every function here is a no-op outside the desktop app.
 *
 * @see https://www.todesktop.com/builder/docs/application/capture-screen-contents
 */

export type DesktopCaptureSource = Awaited<
  ReturnType<typeof app.getCaptureSources>
>[number];

export type DesktopCaptureSourceKind = "screen" | "window";

export const isDesktopApp = (): boolean => platform.todesktop.isDesktopApp();

/** Electron source ids are prefixed `screen:` or `window:`. */
export function getDesktopCaptureSourceKind(
  source: DesktopCaptureSource,
): DesktopCaptureSourceKind {
  return source.id.startsWith("window:") ? "window" : "screen";
}

const thumbnailSize = { width: 320, height: 180 };

export async function getDesktopCaptureSources(): Promise<
  DesktopCaptureSource[]
> {
  return app.getCaptureSources({
    types: ["screen", "window"],
    thumbnailSize,
    fetchWindowIcons: true,
  });
}

export type DesktopScreenAccessStatus = Awaited<
  ReturnType<typeof systemPreferences.getMediaAccessStatus>
>;

/**
 * macOS Screen Recording permission. Windows always reports `granted`. On macOS the
 * OS prompts on the first capture attempt while status is `not-determined`, but a
 * `denied` status cannot be re-prompted from the app.
 */
export async function getDesktopScreenAccessStatus(): Promise<DesktopScreenAccessStatus> {
  return systemPreferences.getMediaAccessStatus("screen");
}

/**
 * Point the next `getDisplayMedia` call (which LiveKit makes internally when a
 * screen share starts) at the chosen source.
 */
export async function selectDesktopScreenShareSource(
  source: DesktopCaptureSource,
): Promise<void> {
  await session.setDisplayMediaRequestHandler(null, (_request, callback) => {
    callback({ video: source });
  });
}
